import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { SparkDataTable } from 'sharedComponents/UI';
import { Column } from 'sharedComponents/UI/SparkDataTable';
import { formatDateAndTime } from 'utilities/time';
import { fileTypeToIconMap } from '../Modals/FileUploadStep';
import { RawFileIcon } from 'components/SVG/fileType';
import { useMemoryByID } from 'hooks/memory/useMemoryByID';
import MemoryFileUpload from '../MemoryFileUpload';
import { downloadFile } from 'utilities/fileUtilities';
import { useDeleteFile } from 'hooks/useDeleteFile';
import { useDialog, useWorkspaceContext } from 'hooks/common';
import ConfirmationDialog from 'components/common/ConfirmationDialog';
import { useGetBlobSasToken } from 'hooks/useGetBlobSasToken';
import FileViewDialog from './FileViewDialog';
import MemoryOptionsModal from 'components/common/MemoryOptionsModal';

type MemoryEditFilesProps = {
  memoryID: string;
};

type Row = {
  id: string;
  fileName: string;
  fileData: string;
  fileContent: string;
  fileSize: string;
  fileType: string;
  azureBLOBURL: string;
  createdAt: string;
  createdBy: string;
};
function bytesToMB(bytes: number): string {
  if (bytes === 0) {
    return '0 MB';
  }
  return (bytes / 1000 / 1000).toFixed(3) + ' MB';
}
function MemoryEditFiles({ memoryID }: MemoryEditFilesProps) {
  const [selectedFileURL, setSelectedFileURL] = React.useState<string | null>(
    null
  );
  const { joinedMemory } = useMemoryByID(memoryID);
  const { mutateAsync: deleteFile } = useDeleteFile();
  const { getWorkspace } = useWorkspaceContext();
  const workSpace = getWorkspace();
  const { refetch } = useGetBlobSasToken(
    workSpace?.attributes?.containerName || 'temp'
  );

  const handleFileDownload = async (url: string) => {
    const { data } = await refetch();
    const token = data?.data.attributes.token;
    downloadFile(`${url}?${token}`);
  };

  const {
    open: isDeleteDialogOpen,
    openDialog: openDeleteDialog,
    closeDialog: closeDeleteDialog,
  } = useDialog();

  const {
    open: isFileViewDialogOpen,
    openDialog: openFileViewDialog,
    closeDialog: closeFileViewDialog,
  } = useDialog();

  const [selectedFileId, setSelectedFileId] = React.useState('');
  const handleDeleteFile = async (fileID: string) => {
    await deleteFile(fileID);
    closeDeleteDialog();
  };

  const handleFileViewClick = (blobURL: string) => {
    setSelectedFileURL(blobURL);
    openFileViewDialog();
  };

  const handleCloseFIleViewDialog = () => {
    setSelectedFileURL(null);
    closeFileViewDialog();
  };

  const columns: Column<Row>[] = [
    {
      field: 'fileType',
      headerText: 'File Type',
      flex: 1.5,
      cellRenderer: (row) => fileTypeToIconMap[row.fileType] || <RawFileIcon />,
    },
    {
      field: 'fileName',
      headerText: 'File Name',
      flex: 3,
      cellRenderer: (row) => (
        <Box>
          <Typography
            variant='h6'
            color={(theme) => theme.palette.common.black}
            sx={{ fontFamily: 'Mont-semibold', textWrap: 'wrap' }}
          >
            {row.fileName}
          </Typography>
          <Typography
            variant='body2'
            sx={{
              color: '#BDBDBD',
              pt: 0.5,
            }}
          >
            {bytesToMB(Number(row.fileSize))}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'createdBy',
      headerText: 'Created By',
      flex: 2,
      cellRenderer: (row) => (
        <Typography
          variant='h6'
          color={(theme) => theme.palette.common.black}
          sx={{ fontFamily: 'Mont-semibold', textWrap: 'wrap' }}
        >
          {formatDateAndTime(row.createdAt, 'DD MMM YYYY HH:mm')}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerText: '',
      flex: 2,
      justifyContent: 'flex-end',
      alignItems: 'flex-end',
      cellRenderer: (row) => (
        <MemoryOptionsModal
          row={row}
          handleFileDownload={handleFileDownload}
          handleFileViewClick={handleFileViewClick}
          handleDeleteFile={(id) => {
            setSelectedFileId(id);
            openDeleteDialog();
          }}
        />
      ),
    },
  ];
  const rows: Row[] | undefined = joinedMemory?.files?.map((memory) => {
    return {
      id: memory?.id || '',
      fileName: memory?.attributes?.fileName || '',
      fileData: memory?.attributes?.fileData || '',
      fileContent: memory?.attributes?.fileContent || '',
      fileSize: memory?.attributes?.fileSize.toString() || '',
      fileType: memory?.attributes?.fileType || '',
      azureBLOBURL:
        memory?.attributes?.url || memory?.attributes?.azureBLOBURL || '',
      createdAt: memory?.attributes?.createdAt || '',
      createdBy: memory?.attributes?.createdBy || '',
    };
  });

  return (
    <Grid container gap={2} pb={2}>
      <Grid item md={12} pl={2} pr={2}>
        <Typography
          variant='body1'
          sx={{
            color: '#404040',
            fontFamily: 'Mont-regular',
            textWrap: 'wrap',
            mb: -4,
          }}
        >
          Add Files to expand Knowledge
          <Typography variant='body2' sx={{ color: '#A3A3A3' }}>
            Files of type .txt, .pdf, .doc, or .docx and up to 15MB are
            currently supported.
          </Typography>
        </Typography>
        <MemoryFileUpload memoryID={memoryID} />
      </Grid>
      <Box p={2}>
        <SparkDataTable<Row>
          columns={columns}
          rows={rows || []}
          isRowClickable={false}
          shouldRenderHeader={false}
        />
      </Box>
      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={closeDeleteDialog}
        onPrimaryButtonClick={() => handleDeleteFile(selectedFileId)}
        title='Confirm Delete'
        confirmationMessage='Are you sure you want to delete this file?'
      />
      {!!selectedFileURL && (
        <FileViewDialog
          fileURL={selectedFileURL}
          open={isFileViewDialogOpen}
          onClose={handleCloseFIleViewDialog}
        />
      )}
    </Grid>
  );
}

export default MemoryEditFiles;
